import { Wind, Flame, Eye, EyeOff, Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import { SCALES, SCALAR_LAYERS, domainFor, gradientCss } from "../lib/colorScales";

const RANGES = [
  { days: 1, label: "24j" },
  { days: 2, label: "48j" },
  { days: 5, label: "5 hari" },
];

const FIRMS_NOTE = {
  ok: "NASA FIRMS · VIIRS S-NPP + NOAA-20",
  no_key: "Data simulasi — tambahkan FIRMS_MAP_KEY untuk data satelit asli",
  invalid_key: "Kunci FIRMS ditolak NASA (Invalid MAP_KEY) — memakai simulasi",
  error: "NASA FIRMS tidak dapat dijangkau — memakai simulasi",
};

const FireSection = ({ fire }) => {
  const { fires, conf, toggleConf, days, setDays, visible, setVisible, loading, error, retry } = fire;
  const shown = fires ? Math.min(fires.shown, 1500) : 0;
  const total = fires
    ? fires.conf_counts
      ? ["l", "n", "h"].reduce((a, k) => a + (conf[k] ? fires.conf_counts[k] || 0 : 0), 0)
      : fires.total
    : 0;
  const countText = !visible
    ? "Titik api disembunyikan"
    : fires
      ? shown >= total
        ? `${total.toLocaleString("id-ID")} titik api`
        : `${shown.toLocaleString("id-ID")}/${total.toLocaleString("id-ID")} titik api`
      : "Titik api";
  return (
    <div className="mt-2.5 pt-2.5 border-t border-border/60" data-testid="legend-fires">
      <div className="flex items-center gap-2">
        <Flame className="w-3.5 h-3.5 text-orange-400" />
        <span className="text-xs font-semibold" data-testid="fire-count">
          {countText} · {RANGES.find((r) => r.days === days)?.label}
        </span>
        {fires?.source === "mock" && (
          <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded-full px-1.5 py-0.5" data-testid="fire-mock-badge">
            Simulasi
          </span>
        )}
        {loading && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
        <button
          data-testid="fire-visibility-toggle"
          onClick={() => setVisible(!visible)}
          title={visible ? "Sembunyikan titik api" : "Tampilkan titik api"}
          className="ml-auto h-6 w-6 grid place-items-center rounded-md hover:bg-secondary/70"
        >
          {visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />}
        </button>
      </div>
      <div className="flex items-center gap-1 mt-2">
        {RANGES.map((r) => (
          <button
            key={r.days}
            data-testid={`fire-range-${r.days}`}
            onClick={() => setDays(r.days)}
            className={`text-[10px] font-semibold px-2 py-1 rounded-full transition-colors ${
              days === r.days ? "bg-orange-500 text-white" : "bg-secondary/60 text-muted-foreground hover:text-foreground"
            }`}
          >
            {r.label}
          </button>
        ))}
        <span className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground">
          <span className="inline-block rounded-full" style={{ width: 7, height: 7, background: "#fb923c" }} />
          <span className="inline-block rounded-full" style={{ width: 9, height: 9, background: "#f97316" }} />
          <span className="inline-block rounded-full" style={{ width: 11, height: 11, background: "#ef4444" }} />
          FRP
        </span>
      </div>
      <div className="flex items-center gap-1.5 mt-2" data-testid="fire-confidence-filter">
        <span className="text-[10px] text-muted-foreground mr-0.5">Kepercayaan</span>
        {[["l", "Rendah"], ["n", "Sedang"], ["h", "Tinggi"]].map(([k, label]) => (
          <button
            key={k}
            data-testid={`fire-conf-${k}`}
            aria-pressed={conf[k]}
            onClick={() => toggleConf(k)}
            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${
              conf[k] ? "bg-orange-500/20 text-orange-300 border-orange-500/50" : "bg-transparent text-muted-foreground border-border/60 line-through"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="text-[10px] text-muted-foreground mt-1.5" data-testid="firms-status">
        {FIRMS_NOTE[fires?.firms_status] || (fires?.source === "firms" ? FIRMS_NOTE.ok : FIRMS_NOTE.no_key)}
      </div>
      <Status loading={false} error={error} retry={retry} />
    </div>
  );
};

const Status = ({ loading, error, retry, loadingText }) => {
  if (error)
    return (
      <div className="text-[10px] text-amber-300 mt-1.5 flex items-center gap-1.5" data-testid="grid-error">
        <AlertTriangle className="w-3 h-3" />
        Gagal memuat data
        <button onClick={retry} className="ml-auto flex items-center gap-1 font-semibold underline-offset-2 hover:underline" data-testid="grid-retry">
          <RefreshCw className="w-3 h-3" />Coba lagi
        </button>
      </div>
    );
  if (loading)
    return (
      <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
        <Loader2 className="w-3 h-3 animate-spin" />{loadingText}
      </div>
    );
  return null;
};

export const MapLegend = ({ layer, grid, scalar, wind, fire, units = "metric" }) => {
  if (SCALAR_LAYERS.includes(layer)) {
    const [lo, hi] = domainFor(layer, grid);
    const conv = (v) => (layer === "temp" && units === "imperial" ? Math.round((v * 9) / 5 + 32) : v);
    const mid = Math.round((lo + hi) / 2);
    const label = layer === "temp" ? `Suhu (${units === "imperial" ? "°F" : "°C"})` : SCALES[layer].label;
    return (
      <div className="windy-legend" data-testid={`legend-${layer}`}>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{label}</div>
        <div className="h-2.5 w-44 rounded-full" style={{ background: gradientCss(layer) }} />
        <div className="flex justify-between text-[10px] num text-muted-foreground mt-1">
          <span>{conv(lo)}</span>
          <span>{conv(mid)}</span>
          <span>{conv(hi)}</span>
        </div>
        {layer === "pressure" && <div className="text-[10px] text-muted-foreground mt-1">Garis isobar tiap 2 hPa (4 hPa saat zoom jauh)</div>}
        {layer === "temp" && <div className="text-[10px] text-muted-foreground mt-1">Skala mengikuti suhu min–maks area peta · Open-Meteo</div>}
        <Status loading={scalar.loading && !grid} error={scalar.error} retry={scalar.retry} loadingText="Memuat grid..." />
      </div>
    );
  }

  if (layer === "wind") {
    const { grid, loading, error, retry, fallback } = wind;
    return (
      <div className="windy-legend" data-testid="legend-wind">
        <div className="flex items-center gap-2">
          <Wind className="w-3.5 h-3.5 text-sky-300" />
          <span className="text-xs font-semibold">Angin 10 m</span>
          <span className="text-[10px] text-muted-foreground">Open-Meteo</span>
          {loading && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground ml-auto" />}
        </div>
        <div className="flex items-center gap-1.5 mt-2 text-[10px] text-muted-foreground num" data-testid="wind-speed-legend">
          {[["<2", "rgba(255,255,255,0.55)"], ["2–5", "#7dd3fc"], ["5–9", "#34d399"], ["9–14", "#fbbf24"], [">14", "#f87171"]].map(([lbl, c]) => (
            <span key={lbl} className="flex items-center gap-1">
              <span className="inline-block w-3 h-1 rounded" style={{ background: c }} />
              {lbl}
            </span>
          ))}
          <span className="ml-auto">m/s</span>
        </div>
        {fallback && grid && (
          <div className="text-[10px] text-amber-300 mt-1.5" data-testid="wind-fallback-note">
            Grid angin tidak tersedia — menampilkan angin seragam dari prakiraan kota aktif
          </div>
        )}
        <Status loading={loading && !grid} error={error && !grid} retry={retry} loadingText="Memuat grid angin..." />
        {error && grid && (
          <button onClick={retry} className="mt-1 text-[10px] text-muted-foreground underline-offset-2 hover:underline flex items-center gap-1" data-testid="grid-retry">
            <RefreshCw className="w-3 h-3" />Coba muat grid lagi
          </button>
        )}
        <FireSection fire={fire} />
      </div>
    );
  }
  return null;
};
